import { motion } from "framer-motion";
import { METRICS } from "@/constants/testIds";

const metrics = [
  { label: "Process Drag Quantified", description: "Every engagement begins with a mathematical baseline — not estimates. You see exactly where capital and time leak before any intervention starts." },
  { label: "Capacity Truth Surfaced", description: "The diagnostic reveals the gap between perceived headcount need and actual transactional demand. Most functions discover they're solving the wrong resourcing problem." },
  { label: "AI-Ready Architecture", description: "The To-Be Target Operating Model is designed for Straight-Through Processing — meaning systems, roles, and data are structured so automation has clean entry points." },
  { label: "Evidence Over Instinct", description: "Every recommendation ties back to observed data: volume, handling time, variance. No opinion-led slide decks. No benchmarks borrowed from someone else's function." },
];

export default function Metrics() {
  return (
    <section
      id="metrics"
      data-testid={METRICS.section}
      className="relative py-24 sm:py-32 lg:py-40 overflow-hidden"
    >
      <div className="absolute inset-0 z-0 bg-themed-secondary" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <motion.p
          data-testid={METRICS.overline}
          className="overline mb-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          Proven Impact
        </motion.p>
        <motion.h2
          data-testid={METRICS.heading}
          className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 max-w-3xl text-themed-primary"
          style={{ fontFamily: "var(--sv-heading-font)" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          What Changes After a <span className="metallic-text">Siveli</span> Engagement
        </motion.h2>
        <p className="text-themed-body text-base sm:text-lg max-w-xl mb-16 leading-relaxed">
          We don't promise percentages. We deliver a measured reality of your function
          and a re-engineered architecture you can defend to the board.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {metrics.map((metric, i) => (
            <motion.div
              key={metric.label}
              data-testid={`metric-card-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group"
            >
              <div className="pt-6 group-hover:border-themed-gold transition-colors duration-300"
                style={{ borderTop: "1px solid var(--sv-divider)" }}>
                <h3 className="text-lg font-semibold text-themed-primary mb-3" style={{ fontFamily: "var(--sv-heading-font)" }}>{metric.label}</h3>
                <p className="text-sm text-themed-muted leading-relaxed">{metric.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* 2026 Advantage */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-24 glass-card p-8 sm:p-12"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-8">
            <div className="flex-1">
              <span className="text-xs text-themed-gold tracking-widest uppercase" style={{ fontFamily: "var(--sv-mono-font)" }}>The 2026 Advantage</span>
              <h3 className="text-2xl sm:text-3xl font-semibold text-themed-primary mt-2 mb-4" style={{ fontFamily: "var(--sv-heading-font)" }}>
                Beyond Traditional BPR
              </h3>
              <p className="text-themed-body leading-relaxed">
                Siveli leverages established BPR theories but updates them for the 2026 landscape.
                While legacy firms still do manual time audits, our framework integrates agentic bots
                and low-code ecosystem triggers directly into HR and People architecture,
                fundamentally shifting how work is executed across the people function.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a
                href="#contact"
                className="btn-shine font-bold tracking-wide px-8 py-4 text-sm transition-colors inline-block whitespace-nowrap"
                style={{ backgroundColor: "var(--sv-gold)", color: "var(--sv-bg-primary)" }}
              >
                See How We Do It
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
